import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

const SOURCE_META = {
  stored: { label: "Live copy", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  resend: { label: "From Resend", color: "bg-sky-50 text-sky-700 border-sky-200" },
  unavailable: { label: "Body unavailable", color: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function EmailPreviewDialog({ open, onOpenChange, recordId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !recordId) return;
    setLoading(true);
    setData(null);
    const token = localStorage.getItem("token");
    axios
      .get(`${API_URL}/emails/${recordId}/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load email preview"))
      .finally(() => setLoading(false));
  }, [open, recordId]);

  const copyHtml = async () => {
    if (!data?.html) return;
    try {
      await navigator.clipboard.writeText(data.html);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Copy failed"); }
  };

  const openInNewTab = () => {
    if (!data?.html) return;
    const blob = new Blob([data.html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const meta = SOURCE_META[data?.source] || SOURCE_META.stored;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[90vh] p-0 flex flex-col" data-testid="email-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 flex-shrink-0">
          <DialogTitle className="text-base font-semibold text-slate-800 pr-6 line-clamp-2">
            {loading ? "Loading email…" : data?.subject || "(no subject)"}
          </DialogTitle>
          {data && (
            <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-slate-500">
              <span><span className="text-slate-400">To:</span> <span className="font-medium text-slate-700">{data.to}</span></span>
              <span className="text-slate-300">·</span>
              <span><span className="text-slate-400">From:</span> <span className="font-medium text-slate-700">{data.from_email}</span></span>
              <span className="text-slate-300">·</span>
              <span>{fmtDate(data.created_at)}</span>
              <Badge variant="outline" className={`text-[10px] font-semibold border ${meta.color}`}>{meta.label}</Badge>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden bg-slate-50">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-400" data-testid="email-preview-loading">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
          ) : data ? (
            <iframe
              title="Email preview"
              srcDoc={data.html}
              sandbox=""
              className="w-full h-full border-0 bg-white"
              data-testid="email-preview-iframe"
            />
          ) : null}
        </div>

        {data && (
          <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-end gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={copyHtml} data-testid="copy-email-html-btn" disabled={!data.html}>
              {copied ? <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy HTML</>}
            </Button>
            <Button variant="outline" size="sm" onClick={openInNewTab} data-testid="open-email-newtab-btn" disabled={!data.html}>
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in new tab
            </Button>
            <Button size="sm" onClick={() => onOpenChange(false)} data-testid="close-email-preview-btn" className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
